import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Label } from '@/shared/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'

interface CancelContractDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  contractName: string
  onConfirm: (
    reason: string,
    cancellationType: 'immediate' | 'end_of_period'
  ) => Promise<void>
  isLoading?: boolean
}

const CANCELLATION_REASONS = [
  { value: 'customer_request', label: 'Customer requested cancellation' },
  { value: 'non_payment', label: 'Non-payment' },
  { value: 'service_downgrade', label: 'Service downgrade' },
  { value: 'end_of_project', label: 'End of project' },
  { value: 'switching_provider', label: 'Switching to another provider' },
  { value: 'budget_constraints', label: 'Budget constraints' },
  { value: 'technical_issues', label: 'Technical issues' },
  { value: 'contract_violation', label: 'Contract violation' },
  { value: 'other', label: 'Other (please specify)' },
]

export function CancelContractDialog({
  open,
  onOpenChange,
  contractName,
  onConfirm,
  isLoading = false,
}: CancelContractDialogProps) {
  const [selectedReason, setSelectedReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [cancellationType, setCancellationType] = useState<
    'immediate' | 'end_of_period'
  >('immediate')

  const handleConfirm = async () => {
    const reason =
      selectedReason === 'other'
        ? customReason
        : CANCELLATION_REASONS.find(r => r.value === selectedReason)?.label ||
          'Canceled by user'

    try {
      await onConfirm(reason, cancellationType)

      // Reset form on success (dialog will close from parent)
      setSelectedReason('')
      setCustomReason('')
      setCancellationType('immediate')
    } catch (_error) {
      // Error handling is done in parent component
      // Dialog stays open so user can retry
    }
  }

  const isFormValid =
    selectedReason &&
    (selectedReason !== 'other' || customReason.trim().length > 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <AlertTriangle className='h-5 w-5 text-orange-500' />
            Cancel Contract
          </DialogTitle>
          <DialogDescription>
            You are about to cancel the contract{' '}
            <strong>"{contractName}"</strong>. This action will affect billing
            and service access.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-6 py-4'>
          {/* Cancellation Type */}
          <div className='space-y-3'>
            <Label className='text-sm font-medium'>Cancellation Type</Label>
            <RadioGroup
              value={cancellationType}
              onValueChange={value =>
                setCancellationType(value as 'immediate' | 'end_of_period')
              }
            >
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='immediate' id='immediate' />
                <Label htmlFor='immediate' className='text-sm'>
                  <div>
                    <div className='font-medium'>Immediate</div>
                    <div className='text-gray-600'>
                      Cancel contract immediately
                    </div>
                  </div>
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='end_of_period' id='end_of_period' />
                <Label htmlFor='end_of_period' className='text-sm'>
                  <div>
                    <div className='font-medium'>End of Period</div>
                    <div className='text-gray-600'>
                      Cancel at the end of current billing period
                    </div>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Cancellation Reason */}
          <div className='space-y-3'>
            <Label htmlFor='reason' className='text-sm font-medium'>
              Cancellation Reason *
            </Label>
            <Select value={selectedReason} onValueChange={setSelectedReason}>
              <SelectTrigger>
                <SelectValue placeholder='Select a reason for cancellation' />
              </SelectTrigger>
              <SelectContent>
                {CANCELLATION_REASONS.map(reason => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Reason Input */}
          {selectedReason === 'other' && (
            <div className='space-y-3'>
              <Label htmlFor='custom-reason' className='text-sm font-medium'>
                Please specify the reason *
              </Label>
              <Textarea
                id='custom-reason'
                placeholder='Enter the specific reason for cancellation...'
                value={customReason}
                onChange={e => setCustomReason(e.target.value)}
                rows={3}
                className='resize-none'
              />
            </div>
          )}

          {/* Warning Message */}
          <div className='bg-orange-50 border border-orange-200 rounded-lg p-3'>
            <div className='flex items-start gap-2'>
              <AlertTriangle className='h-4 w-4 text-orange-500 mt-0.5' />
              <div className='text-sm text-orange-800'>
                <div className='font-medium mb-1'>Important:</div>
                <ul className='text-xs space-y-1'>
                  <li>
                    •{' '}
                    {cancellationType === 'immediate'
                      ? 'Service will be terminated immediately'
                      : 'Service will continue until the end of the current billing period'}
                  </li>
                  <li>
                    •{' '}
                    {cancellationType === 'immediate'
                      ? 'No refunds for partial periods'
                      : 'You will be charged for the full current period'}
                  </li>
                  <li>• This action cannot be undone</li>
                  <li>• Customer will be notified of the cancellation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Keep Contract
          </Button>
          <Button
            variant='destructive'
            onClick={handleConfirm}
            disabled={!isFormValid || isLoading}
          >
            {isLoading ? 'Cancelling...' : 'Cancel Contract'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
